<template>
    <div class="sessPlayer">
        <button
            class="delete backBtn"
            aria-label="close"
            title="Back to Recordings"
            @click="$emit('close-click')"
        ></button>
        <div class="player" ref="player">
            <div class="playerError" v-if="!canPlay">
                <b-icon icon="exclamation-circle"></b-icon>
                No recording available.
            </div>
        </div>
        <b-dropdown
            v-if="canPlay"
            class="shareMenu"
            ref="share"
            position="is-top-right"
            aria-role="menu"
            trap-focus
            @active-change="handleShareBoxActiveChange"
        >
            <template #trigger>
                <b-button type="is-white">
                    <i class="fas fa-share"></i>&nbsp;Share
                </b-button>
            </template>
            <b-dropdown-item
                aria-role="menu-item"
                :focusable="false"
                custom
                paddingless
            >
                <div class="modal-card">
                    <section class="modal-card-body">
                        <button
                            class="delete"
                            aria-label="close"
                            @click="closeShareBox"
                        ></button>
                        <b-switch v-model="share.timeOn">
                            Start at: {{ currentTime | millisToHhMmSs }}
                        </b-switch>
                    </section>
                    <footer class="modal-card-foot">
                        <div class="field shareLink" v-show="share.link">
                            <div class="control">
                                <b-tooltip
                                    :label="share.copyTipText"
                                    position="is-bottom"
                                    class="shareLinkCopyBtn"
                                >
                                    <b-button
                                        icon-left="copy"
                                        type="is-white"
                                        size="is-small"
                                        @click="copyShareLink"
                                    />
                                </b-tooltip>
                                <input
                                    type="text"
                                    class="input"
                                    ref="shareLink"
                                    readonly
                                    v-model="share.link"
                                />
                            </div>
                        </div>
                        <b-button
                            type="is-link"
                            :loading="share.loading"
                            v-show="!share.link"
                            @click="createShareLink"
                        >
                            <i class="fas fa-link"></i> Create Share Link
                        </b-button>
                    </footer>
                </div>
            </b-dropdown-item>
        </b-dropdown>
    </div>
</template>

<script>
import { rrwebPlayer } from '~/assets/js/rrweb-player'

function calcWidth() {
    return window.innerWidth * 0.75
}

function calcHeight() {
    return window.innerHeight - 200
}

let playerObj = null

export default {
    name: 'SessionPlayer',
    props: {
        rec: {
            type: Object,
        },
        initialTime: {
            type: Number,
        },
    },
    data() {
        return {
            loaded: false,
            frameWidth: 0,
            frameHeight: 0,
            currentTime: this.initialTime || 0,
            width: calcWidth(),
            height: calcHeight(),
            share: {
                timeOn: true,
                loading: false,
                link: '',
                copyTipText: 'Copy',
            },
        }
    },
    computed: {
        events() {
            return this.rec.events
        },
        canPlay() {
            return this.events && this.events.length > 1
        },
    },
    beforeDestroy() {
        playerObj && playerObj.pause()
        playerObj = null
    },
    mounted() {
        this.resize()
        window.addEventListener('resize', this.resize)
        this.canPlay && this.init()
    },
    methods: {
        init() {
            playerObj = new rrwebPlayer({
                target: this.$refs.player,
                props: {
                    events: this.events,
                    mouseTail: false,
                    showWarning: false,
                    autoPlay: true,
                    width: this.width,
                    height: this.height,
                    logConfig: {
                        level: [],
                    },
                },
            })
            this.addListeners()
            playerObj.goto(this.currentTime)
        },
        addListeners() {
            playerObj.addEventListener('event-cast', () => {
                if (!this.loaded) {
                    this.loaded = true
                    this.$refs.player
                        .getElementsByClassName('rr-controller__btns')[0]
                        .appendChild(this.$refs.share.$el)
                }
            })
            playerObj.addEventListener('ui-update-current-time', event => {
                this.currentTime = Math.floor(event.payload)
            })
            playerObj.addEventListener('resize', dimension => {
                this.frameWidth = dimension.width
                this.frameHeight = dimension.height
                this.rescale()
            })
            window.addEventListener('sessionPlayer.goToTime', event => {
                playerObj.goto(event.detail.time)
                playerObj.play()
            })
        },
        handleShareBoxActiveChange(active) {
            if (active) {
                this.share.link = ''
                this.share.timeOn = true
                playerObj.pause()
            } else {
                playerObj.play()
                this.share.loading = false
            }
        },
        closeShareBox() {
            this.$refs.share.toggle()
        },
        async createShareLink() {
            this.share.loading = true
            const { success, data } = await this.$api.get(
                [
                    `/v1/account/recs/${this.rec.id}/sharelink`,
                    this.share.timeOn ? `/${this.currentTime}` : '',
                ].join('')
            )
            this.share.loading = false
            if (success) {
                this.share.link = data.shortUrl
            } else {
                this.$toast.error()
            }
        },
        copyShareLink() {
            this.$refs.shareLink.select()
            document.execCommand('copy')
            this.share.copyTipText = 'Copied'
            setTimeout(() => {
                this.share.copyTipText = 'Copy'
            }, 1000)
        },
        resize() {
            this.width = calcWidth()
            this.height = calcHeight()
            const pxWidth = this.width + 'px'
            const pxHeight = this.height + 'px'
            this.$refs.player.style.minWidth = pxWidth
            this.$refs.player.style.minHeight = pxHeight
            if (!this.loaded) {
                return
            }
            const playerEl = this.$refs.player.getElementsByClassName(
                'rr-player'
            )[0]
            const frameEl = playerEl.getElementsByClassName(
                'rr-player__frame'
            )[0]
            playerEl.style.width = pxWidth
            playerEl.style.height = pxHeight
            frameEl.style.width = pxWidth
            frameEl.style.height = pxHeight
            // playerObj.$set({
            //     width: this.width,
            //     height: this.height,
            // })
            // playerObj.triggerResize()
            this.rescale()
        },
        rescale() {
            const frameWidth = this.frameWidth || this.rec.screen.width
            const frameHeight = this.frameHeight || this.rec.screen.height
            const scaleWidth = this.width / frameWidth
            const scaleHeight = (this.height - 80) / frameHeight // Minus controller height (80)
            const scale = Math.min(scaleWidth, scaleHeight, 1)
            const wrapperEl = document.getElementsByClassName(
                'replayer-wrapper'
            )[0]
            wrapperEl.style.transform = `scale(${scale}) translate(-50%, 0%)`
            // Set max height
            wrapperEl.style.height = `${this.height / scale}px`
        },
    },
}
</script>
